import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { CheckCircle, Download, FileText, Video, ArrowRight } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ThankYou = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [orderData, setOrderData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrderDetails();
  }, [orderId]);

  const fetchOrderDetails = async () => {
    try {
      const response = await axios.get(`${API}/orders/${orderId}`);
      setOrderData(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching order:', error);
      toast.error('Failed to load order details');
      setLoading(false);
    }
  };

  const handleDownload = (url, filename) => {
    window.open(url, '_blank');
    toast.success(`Downloading ${filename}...`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f5f3f8] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-teal-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your order...</p>
        </div>
      </div>
    );
  }

  if (!orderData) {
    return (
      <div className="min-h-screen bg-[#f5f3f8] flex items-center justify-center">
        <div className="text-center">
          <p className="text-2xl text-gray-700 mb-4">Order not found</p>
          <Button onClick={() => navigate('/courses')} className="bg-gradient-to-r from-teal-600 to-purple-600 text-white rounded-full px-8">
            Browse Courses
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="thank-you-page min-h-screen bg-[#f5f3f8] py-20">
      <div className="container mx-auto px-6">
        <div className="max-w-4xl mx-auto">
          {/* Success Message */}
          <div className="bg-white rounded-3xl shadow-2xl p-12 mb-8 text-center" data-testid="success-message">
            <CheckCircle className="w-24 h-24 text-teal-600 mx-auto mb-6" />
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-800 mb-4">Thank You for Your Purchase!</h1>
            <p className="text-xl text-gray-600 mb-2">Your payment was successful</p>
            <p className="text-lg text-gray-600">Order ID: <span className="font-semibold text-teal-600">{orderData.order.id}</span></p>
            {orderData.order.customer_email && (
              <p className="text-md text-gray-500 mt-4">
                Course access details have been sent to: <span className="font-semibold">{orderData.order.customer_email}</span>
              </p>
            )}
          </div>

          {/* Course Materials */}
          <div className="bg-white rounded-3xl shadow-2xl p-8">
            <h2 className="text-3xl font-bold text-gray-800 mb-6 bg-gradient-to-r from-teal-500 via-blue-500 to-purple-500 bg-clip-text text-transparent">
              Your Course Materials
            </h2>
            <p className="text-gray-600 mb-8">Download your course videos and PDFs below. These links are also available in your email.</p>

            {orderData.course_materials && orderData.course_materials.length > 0 ? (
              <div className="space-y-8">
                {orderData.course_materials.map((courseItem, courseIndex) => (
                  <div key={courseIndex} className="border-2 border-gray-200 rounded-2xl p-6" data-testid={`course-material-${courseIndex}`}>
                    <h3 className="text-2xl font-bold text-gray-800 mb-6">{courseItem.course_title}</h3>
                    
                    <div className="grid md:grid-cols-2 gap-6">
                      {Object.keys(courseItem.materials).map((moduleKey) => {
                        const module = courseItem.materials[moduleKey];
                        return (
                          <div key={moduleKey} className="bg-gradient-to-br from-[#f0f9ff] to-[#f5f3f8] p-6 rounded-xl">
                            <h4 className="font-bold text-lg text-gray-800 mb-4">{module.title}</h4>
                            
                            {/* Video Download */}
                            <div className="mb-4">
                              <Button
                                data-testid={`download-video-${moduleKey}`}
                                onClick={() => handleDownload(module.video_url, `${module.title}-video.mp4`)}
                                className="w-full bg-gradient-to-r from-teal-600 to-blue-600 hover:from-teal-700 hover:to-blue-700 text-white rounded-lg py-3 flex items-center justify-center gap-2"
                              >
                                <Video className="w-5 h-5" />
                                Download Video
                              </Button>
                            </div>
                            
                            {/* PDF Download */}
                            <div>
                              <Button
                                data-testid={`download-pdf-${moduleKey}`}
                                onClick={() => handleDownload(module.pdf_url, `${module.title}-notes.pdf`)}
                                className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white rounded-lg py-3 flex items-center justify-center gap-2"
                              >
                                <FileText className="w-5 h-5" />
                                Download PDF
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-600">No course materials available for this order.</p>
            )}
          </div>

          {/* Action Buttons */}
          <div className="mt-8 flex justify-center gap-4">
            <Button
              data-testid="browse-more-courses-btn"
              onClick={() => navigate('/courses')}
              className="bg-gradient-to-r from-teal-600 to-purple-600 hover:from-teal-700 hover:to-purple-700 text-white px-8 py-6 text-lg rounded-full"
            >
              Browse More Courses <ArrowRight className="ml-2" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ThankYou;
